import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { FinancialService } from '../../../../services/financial.service';
import { CustomerService } from '../../../../services/customer.service';
import { CompanyService } from '../../../../services/company.service';
import { SupplierService } from '../../../../services/supplier.service';
import { EmployeeService } from '../../../../services/employee.service';
import { FinancialSummary } from '../../../../models/financialSummaryDto.model';
import { Customer } from '../../../../models/customer.model';
import { Company } from '../../../../models/company.model';
import { Supplier } from '../../../../models/supplier.model';
import { Employee } from '../../../../models/employee.model';
import { RestResponse } from '../../../../models/restResponse.mode';
import { DateRangeEnum } from '../../../../models/enums/dateRange.enum';
import { ExpenseTypeEnum } from '../../../../models/enums/expenseType.enum';
import { FinancialInstallment } from '../../../../models/financialInstallment.model';
import { ModalComponent } from '../../../../components/modal/modal.component';
import { TypePaymentService } from '../../../../services/typePayment.service';
import { TypePayment } from '../../../../models/typePayment.model';
import { CreateFinancialPaymentDto } from '../../../../models/financialPayment.model';
import { InstallmentSettlementPreviewDto } from '../../../../models/installment-settlement-preview.model';
import { SupplierExpenseSummaryDto } from '../../../../models/supplierExpenseSummary.model';
import { EmployeeExpenseSummaryDto } from '../../../../models/employeeExpenseSummary.model';
import { CompanyExpenseSummaryDto } from '../../../../models/companyExpenseSummary.model';

@Component({
  selector: 'app-view-ecs-expenses',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule,
    InputTextModule,
    DropdownModule,
    CardModule,
    ButtonModule,
    PaginatorModule,
    TableModule,
    TagModule,
    TooltipModule,
    ModalComponent
  ],
  templateUrl: './list-ecs-expenses.component.html'
})
export class ListEcsExpensesComponent implements OnInit {
  entityName = 'Carregando...';
  entityId: number = 0;
  expenseAccountType: string = '';
  entity: Customer | Company | Supplier | Employee | null = null;
  total = 0;
  expenses: FinancialSummary[] = [];
  loading = false;
  
  // Paginação
  first = 0;
  rows = 10;
  totalRecords = 0;
  currentPage = 1;
  
  // Filtros
  searchTerm = '';
  currentStatus = 'todos';
  currentDateRange = DateRangeEnum.All;
  // Expansão e parcelas
  expanded: Record<number, boolean> = {};
  installmentsCache: Record<number, FinancialInstallment[]> = {};
  installmentsLoading: Record<number, boolean> = {};

  // Modal de quitação
  settleDialogVisible = false;
  settleSubmitting = false;
  selectedInstallment: FinancialInstallment | null = null;
  typePayments: TypePayment[] = [];
  typePaymentDropdownOptions: any[] = [];
  settleDate: string = new Date().toISOString().substring(0, 10);
  settleTypePaymentId: number | null = null;
  settleValue: number | null = null;
  settlePreview: InstallmentSettlementPreviewDto | null = null;
  settlePreviewLoading = false;
  
  // Opções de filtro
  statusOptions = [
    { label: 'Todos', value: 'todos' },
    { label: 'Pendente', value: 'pendente' },
    { label: 'Quitado', value: 'quitado' },
    { label: 'Cancelado', value: 'cancelado' }
  ];
  
  dateRangeOptions = [
    { label: 'Todos', value: DateRangeEnum.All },
    { label: 'Este mês', value: DateRangeEnum.ThisMonth },
    { label: 'Mês passado', value: DateRangeEnum.LastMonth },
    { label: 'Últimos 3 meses', value: DateRangeEnum.LastThreeMonths },
    { label: 'Últimos 6 meses', value: DateRangeEnum.LastSixMonths },
    { label: 'Este ano', value: DateRangeEnum.ThisYear },
    { label: 'Ano passado', value: DateRangeEnum.LastYear }
  ];

  constructor(
    private financialService: FinancialService,
    private customerService: CustomerService,
    private companyService: CompanyService,
    private supplierService: SupplierService,
    private employeeService: EmployeeService,
    private typePaymentService: TypePaymentService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    // Inicializar valores de paginação
    this.first = 0;
    this.rows = 10;
    this.totalRecords = 0;
    this.currentPage = 1;
    
    // Pegar o entityId e expenseAccountType da rota
    this.route.params.subscribe(params => {
      this.entityId = +params['id']; // Converte para number
      this.expenseAccountType = params['expenseAccountType']; // 'Company', 'Supplier', ou 'Employee'
      
      this.loadEntity();
      this.loadExpenses();
    });

    this.loadTypePayments();
  }

  loadEntity(): void {
    switch (this.expenseAccountType) {
      case 'Company':
        this.loadCompany();
        break;
      case 'Supplier':
        this.loadSupplier();
        break;
      case 'Employee':
        this.loadEmployee();
        break;
      default:
        this.entityName = 'Tipo inválido';
    }
  }

  loadCompany(): void {
    this.companyService.find(this.entityId).subscribe({
      next: (response: RestResponse<Company>) => {
        if (response && response.data) {
          this.entity = response.data;
          this.entityName = response.data.razaoSocial;
        } else {
          this.entityName = 'Empresa não encontrada';
        }
      },
      error: (error) => {
        this.entityName = 'Empresa não encontrada';
      }
    });
  }

  loadSupplier(): void {
    this.supplierService.find(this.entityId).subscribe({
      next: (response: RestResponse<Supplier>) => {
        if (response && response.data) {
          this.entity = response.data;
          this.entityName = response.data.companyName;
        } else {
          this.entityName = 'Fornecedor não encontrado';
        }
      },
      error: (error) => {
        this.entityName = 'Fornecedor não encontrado';
      }
    });
  }

  loadEmployee(): void {
    this.employeeService.find(this.entityId).subscribe({
      next: (response: RestResponse<Employee>) => {
        if (response && response.data) {
          this.entity = response.data;
          this.entityName = response.data.fullName;
        } else {
          this.entityName = 'Funcionário não encontrado';
        }
      },
      error: (error) => {
        this.entityName = 'Funcionário não encontrado';
      }
    });
  }

  loadTypePayments(): void {
    this.typePaymentService.getAll().subscribe({
      next: (response) => {
        if (response && response.data) {
          this.typePayments = response.data;
          this.typePaymentDropdownOptions = [
            { label: 'Selecione um método de pagamento', value: '' },
            ...this.typePayments.map(tp => ({ label: tp.description, value: tp.id }))
          ];
        } else {
          this.typePayments = [];
          this.typePaymentDropdownOptions = [
            { label: 'Selecione um método de pagamento', value: '' }
          ];
        }
      },
      error: () => {
        this.typePayments = [];
        this.typePaymentDropdownOptions = [
          { label: 'Selecione um método de pagamento', value: '' }
        ];
      }
    });
  }

  private getExpenseTypeFromAccountType(accountType: string): ExpenseTypeEnum {
    switch (accountType) {
      case 'Company':
        return ExpenseTypeEnum.Company;
      case 'Supplier':
        return ExpenseTypeEnum.Supplier;
      case 'Employee':
        return ExpenseTypeEnum.Employee;
      default:
        return ExpenseTypeEnum.Company;
    }
  }

  private convertToFinancialSummary(data: SupplierExpenseSummaryDto[] | EmployeeExpenseSummaryDto[] | CompanyExpenseSummaryDto[]): FinancialSummary[] {
    // Converter os dados para o formato FinancialSummary
    return data.map((item: any) => ({
      id: item.id || 0,
      code: item.code || '',
      installments: item.installments || 0,
      dateTime: item.dateTime || new Date(),
      installmentsCount: item.installments || 0, // Usar installments como installmentsCount
      total: item.total || 0,
      status: item.status || 'pendente'
    }));
  }



  loadExpenses(): void {
    this.loading = true;
    
    // Mapear o expenseAccountType para o ExpenseTypeEnum
    const expenseType = this.getExpenseTypeFromAccountType(this.expenseAccountType);
    
    this.financialService.getExpenseSummary(
      this.searchTerm, 
      this.currentStatus, 
      this.currentPage,
      this.entityId,
      this.currentDateRange,
      expenseType
    ).subscribe({
      next: (response: RestResponse<SupplierExpenseSummaryDto[] | EmployeeExpenseSummaryDto[] | CompanyExpenseSummaryDto[]>) => {
        if (response && response.data && Array.isArray(response.data)) {
          // Converter os dados para o formato esperado pelo componente
          this.expenses = this.convertToFinancialSummary(response.data);
          // Usar a estrutura correta da paginação
          if (response.pagination) {
            this.totalRecords = response.pagination.totalRecords || 0;
            this.currentPage = response.pagination.currentPage || 1;
          } else {
            this.totalRecords = 0;
            this.currentPage = 1;
          }
        } else {
          this.expenses = [];
          this.totalRecords = 0;
          this.currentPage = 1;
        }
        this.calculateTotal();
        this.loading = false;
      },
      error: (error) => {
        this.expenses = [];
        this.totalRecords = 0;
        this.currentPage = 1;
        this.calculateTotal();
        this.loading = false;
      }
    });
  }

  calculateTotal(): void {
    if (this.expenses && Array.isArray(this.expenses)) {
      this.total = this.expenses.reduce((sum, item) => sum + item.total, 0);
    } else {
      this.total = 0;
    }
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'quitado':
        return 'success';
      case 'pendente':
        return 'warn';
      case 'cancelado':
        return 'danger';
      default:
        return 'info';
    }
  }

  // Filtros
  onSearchChange(): void {
    this.currentPage = 1;
    this.first = 0;
    this.loadExpenses();
  }

  onStatusChange(): void {
    this.currentPage = 1;
    this.first = 0;
    this.loadExpenses();
  }

  onDateRangeChange(): void {
    this.currentPage = 1;
    this.first = 0;
    this.loadExpenses();
  }

  // Paginação
  onPageChange(event: any): void {
    this.first = event.first || 0;
    this.rows = event.rows || 10;
    this.currentPage = Math.floor(this.first / this.rows) + 1;
    this.loadExpenses();
  }

  // Ações
  print(id: number) {
    // Implementar impressão
  }

  cancel(id: number) {
    // abre modal de confirmação
    this.cancelTargetId = id;
    this.cancelDialogVisible = true;
  }

  // Modal: abrir, confirmar, fechar
  openSettleModal(inst: FinancialInstallment): void {
    if (inst.paid || inst.canceled) return;
    this.selectedInstallment = inst;
    this.settleDate = new Date().toISOString().substring(0, 10);
    this.settleTypePaymentId = null;
    this.settleValue = inst.remainingValue ?? inst.value;
    this.settlePreview = null;
    this.settleDialogVisible = true;
    this.refreshSettlePreview();
  }

  closeSettleModal(): void {
    this.settleDialogVisible = false;
    this.settlePreview = null;
    this.settlePreviewLoading = false;
  }

  onSettleDateChange(): void {
    this.refreshSettlePreview();
  }

  refreshSettlePreview(): void {
    if (!this.selectedInstallment) {
      return;
    }
    this.settlePreviewLoading = true;
    this.financialService
      .settlementPreview(this.selectedInstallment.financialId, this.selectedInstallment.id, this.settleDate)
      .subscribe({
        next: (resp) => {
          this.settlePreview = resp.data ?? null;
          this.settlePreviewLoading = false;
          if (this.settlePreview) {
            this.settleValue = this.settlePreview.suggestedTotal;
          }
        },
        error: () => {
          this.settlePreview = null;
          this.settlePreviewLoading = false;
          this.settleValue = this.getSettleRemainingBefore();
        },
      });
  }

  confirmSettle(): void {
    if (!this.selectedInstallment || this.settleTypePaymentId == null || this.settleValue == null) {
      return;
    }
    if (this.settlePreviewLoading) {
      return;
    }
    const payload: CreateFinancialPaymentDto = {
      FinancialId: this.selectedInstallment.financialId,
      InstallmentId: this.selectedInstallment.id,
      Date: new Date(this.settleDate),
      TypePaymentId: this.settleTypePaymentId,
      ValuePaied: this.settleValue
    };
    this.settleSubmitting = true;
    this.installmentsLoading[this.selectedInstallment.financialId] = true;
    this.financialService.settleInstallment(payload).subscribe({
      next: () => {
        const financialId = this.selectedInstallment!.financialId;
        this.financialService.getInstallments(financialId).subscribe({
          next: (resp) => {
            const list = resp?.data ?? [];
            list.sort((a: any, b: any) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
            this.installmentsCache[financialId] = list;
            this.installmentsLoading[financialId] = false;
          },
          error: () => {
            this.installmentsLoading[financialId] = false;
          }
        });
        this.loadExpenses();
        this.settleSubmitting = false;
        this.settleDialogVisible = false;
      },
      error: () => {
        this.settleSubmitting = false;
        this.installmentsLoading[this.selectedInstallment!.financialId] = false;
      }
    });
  }

  getSettleOriginalValue(): number {
    return this.selectedInstallment ? Number(this.selectedInstallment.value ?? 0) : 0;
  }

  getSettleRemainingBefore(): number {
    if (!this.selectedInstallment) return 0;
    const original = this.getSettleOriginalValue();
    const remaining = this.selectedInstallment.remainingValue != null
      ? Number(this.selectedInstallment.remainingValue)
      : original;
    return Math.max(0, remaining);
  }

  getSettlePaidSoFar(): number {
    const original = this.getSettleOriginalValue();
    const remaining = this.getSettleRemainingBefore();
    return Math.max(0, original - remaining);
  }

  getSettleRemainingPercent(): number {
    const original = this.getSettleOriginalValue();
    const remaining = this.getSettleRemainingBefore();
    if (original <= 0) return 0;
    return Math.max(0, Math.min(100, +(remaining * 100 / original).toFixed(2)));
  }

  getSettleRemainingAfter(): number {
    const remainingBefore = this.getSettleRemainingBefore();
    const interest = this.settlePreview?.interestAmount ?? 0;
    const payNow = Number(this.settleValue ?? 0);
    const principalPortion = Math.max(0, payNow - interest);
    return Math.max(0, remainingBefore - (Number.isNaN(principalPortion) ? 0 : principalPortion));
  }

  getSettleMinPayable(): number {
    const interest = this.settlePreview?.interestAmount ?? 0;
    if (interest > 0) {
      return Math.round(interest * 100) / 100;
    }
    return 0.01;
  }

  getSettleMaxPayable(): number {
    const remainingBefore = this.getSettleRemainingBefore();
    const interest = this.settlePreview?.interestAmount ?? 0;
    return Math.round((remainingBefore + interest) * 100) / 100;
  }

  onSettleValueChange(value: any): void {
    const parsed = Number(value ?? 0);
    if (Number.isNaN(parsed)) {
      this.settleValue = this.getSettleMinPayable();
      return;
    }
    const min = this.getSettleMinPayable();
    const max = this.getSettleMaxPayable();
    this.settleValue = Math.max(min, Math.min(max, parsed));
  }

  enforceSettleInput(event: any): void {
    const inputEl = event?.target as HTMLInputElement;
    if (!inputEl) return;
    const parsed = Number(inputEl.value ?? 0);
    const min = this.getSettleMinPayable();
    const max = this.getSettleMaxPayable();
    const clamped = Math.max(min, Math.min(max, Number.isNaN(parsed) ? min : parsed));
    if (parsed !== clamped) {
      inputEl.value = String(clamped);
    }
    this.settleValue = clamped;
  }

  viewDetails(id: number) {
    // Alterna expansão
    const isExpanding = !this.expanded[id];
    this.expanded[id] = isExpanding;

    if (isExpanding && !this.installmentsCache[id]) {
      this.installmentsLoading[id] = true;
      this.financialService.getInstallments(id).subscribe({
        next: (resp) => {
          this.installmentsCache[id] = resp?.data ?? [];
          this.installmentsLoading[id] = false;
        },
        error: () => {
          this.installmentsCache[id] = [];
          this.installmentsLoading[id] = false;
        }
      });
    }
  }

  createExpense() {
    this.router.navigate(['/app/financial/expenses/new'], {
      queryParams: {
        expenseAccountType: this.expenseAccountType,
        entityId: this.entityId
      }
    });
  }

  // Cancel modal state/handlers
  cancelDialogVisible = false;
  cancelSubmitting = false;
  cancelTargetId: number | null = null;
  closeCancelModal(): void {
    this.cancelDialogVisible = false;
    this.cancelTargetId = null;
  }
  confirmCancel(): void {
    if (this.cancelTargetId == null) { return; }
    this.cancelSubmitting = true;
    this.financialService.cancel(this.cancelTargetId).subscribe({
      next: () => {
        this.cancelSubmitting = false;
        this.cancelDialogVisible = false;
        this.cancelTargetId = null;
        // Atualiza a listagem e dashboard
        this.loadExpenses();
      },
      error: () => {
        this.cancelSubmitting = false;
      }
    });
  }
}
